/**
 * Centralized password validation utilities
 * 
 * This module provides consistent password validation across the application.
 * Password requirements:
 * - At least 8 characters long
 * - At least one lowercase letter
 * - At least one uppercase letter
 * - At least one number
 * - At least one symbol
 */

export interface PasswordValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Validates a password and returns validation result with errors
 * @param password - The password to validate
 * @returns Object with valid boolean and array of error messages
 */
export function validatePassword(password: string): PasswordValidationResult {
  const errors: string[] = [];
  
  if (password.length < 8) {
    errors.push("Password must be at least 8 characters long");
  }
  
  if (!/(?=.*[a-z])/.test(password)) {
    errors.push("Password must contain at least one lowercase letter");
  }
  
  if (!/(?=.*[A-Z])/.test(password)) {
    errors.push("Password must contain at least one uppercase letter");
  }
  
  if (!/\d/.test(password)) {
    errors.push("Password must contain at least one number");
  }
  
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push("Password must contain at least one symbol");
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validates a password and returns the first error message or null if valid
 * Useful for simple validation that only needs one error message
 * @param password - The password to validate
 * @returns Error message string or null if valid
 */
export function validatePasswordSimple(password: string): string | null {
  if (!password) return 'Password is required';
  if (password.length < 8) return 'Password must be at least 8 characters long';
  if (!/(?=.*[a-z])/.test(password)) return 'Password must contain at least one lowercase letter';
  if (!/(?=.*[A-Z])/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/(?=.*\d)/.test(password)) return 'Password must contain at least one number';
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) return 'Password must contain at least one symbol';
  return null;
}

/**
 * Password requirements message for display in UI
 */
export const PASSWORD_REQUIREMENTS_MESSAGE = 
  "Password must be at least 8 characters with one uppercase letter, one lowercase letter, one number, and one symbol";

/**
 * Password requirements as a short description
 */
export const PASSWORD_REQUIREMENTS_SHORT = 
  "8+ chars, uppercase, lowercase, number, symbol";

